import { Injectable } from '@angular/core'
import { Network } from '@capacitor/network'
import { AlertController, NavController } from '@ionic/angular'
import { TranslationService } from './translation.service'
import { route } from 'src/environments/route'

@Injectable({
  providedIn: 'root'
})
export class NetworkService {

  private alert;
  private pendingFunctions: (() => void)[] = [];
  private isExecutingPendingFunctions = false;

  translations = {
    alertTitle: 'Alert',
    alertSubHeader: 'Connection failure',
    alertMessage: 'It seems you are not connected to internet. Come closer to the surface or use the Bepark Wifi if available.'
  };

  constructor(
      private alertController: AlertController,
      private translationService: TranslationService,
      private navController: NavController,
  ) {
    this.translationService.loadTranslation('alert', this.translations);
  }

  async presentConnectionAlert() {
    this.alert = await this.alertController.create({
      header: this.translations.alertTitle,
      subHeader: this.translations.alertSubHeader,
      message: this.translations.alertMessage,
      buttons: ['OK']
    });
    await this.alert.present();
  }

  // This method only checks if the device is connected to a network, not if it has real internet access.
  public async isNetworkAvailable(): Promise<boolean> {
    try {
      const status = await Network.getStatus();
      return status.connected;
    } catch {
      // Fallback to navigator.onLine if Capacitor Network fails
      return navigator.onLine;
    }
  }

  private async testInternetAccess(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);

      const res = await fetch('https://www.google.com/generate_204', {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors'
      });

      clearTimeout(timeout);
      return true;
    } catch (e) {
      this.navController.navigateRoot(route.offline);
      return false;
    }
  }

  // This method never throws an error; it always returns a boolean  
  public async isConnectedToTheInternet(): Promise<boolean> {
    if (await this.isNetworkAvailable()) {
      return await this.testInternetAccess();
    } 
    return false;
  }

  public async runWhenOnline(func: () => void) {
    const isOnline = await this.isConnectedToTheInternet();
    
    if (isOnline) {
      func();
    } else {
      this.pendingFunctions.push(func);
      // We run the executePendingFunctions loop here too in case there is a Network but no Internet
      void this.executePendingFunctions();
    }
  }

  private async executePendingFunctions(): Promise<void> {
    if (this.isExecutingPendingFunctions) {
      return;
    }
    this.isExecutingPendingFunctions = true;
    
    try {
      while (this.pendingFunctions.length > 0) {
        if (! await this.isNetworkAvailable()) {
          break;
        }

        const isOnline = await this.isConnectedToTheInternet();

        if (isOnline) {        
          // Clear the whole queue
          const functionsToExecute = [...this.pendingFunctions];
          this.pendingFunctions = [];
          
          functionsToExecute.forEach(func => func());
        } else {
          // Wait 2 seconds before checking again
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    } finally {
      this.isExecutingPendingFunctions = false;
    }
  }

  public startListeningOnConnection() {

    Network.addListener('networkStatusChange', async (connectionStatus) => {
      // 'wifi' | 'cellular' | 'none' | 'unknown'
      const networkType = connectionStatus.connectionType;

      if(networkType !== 'none'){
        // We just got a connection but we need to wait briefly
        // before we determine the connection type. Might need to wait.
        // prior to doing any api requests as well.
        setTimeout(() => {
          if (this.alert) {
            this.alert.dismiss();
          }
        }, 3000);
      } else {
        void this.presentConnectionAlert();
      }

      if (connectionStatus.connected) {
        void this.executePendingFunctions();
      } else {
        void this.navController.navigateRoot(route.offline);
      }
    });
  }
}
